import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
    Button,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Select,
    Space,
    TimePicker,
    Typography,
    message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { ref, set } from "firebase/database";
import { database } from "../../firebaseConfig";

const CONN_KEY = process.env.REACT_APP_NOT_SECRET_CODE_R;
const CONN_KEY_R = process.env.REACT_APP_NOT_SECRET_CODE_RR;

// Mirrors Map.js#resolveFirebasePath — same scheme so the live map components see the bron.
const resolveFirebasePath = (masa, selectedFlourId) => {
    if (
        selectedFlourId.startsWith("L") ||
        selectedFlourId.startsWith("B") ||
        selectedFlourId.startsWith("R")
    ) {
        return `reservationsL2/${masa}`;
    }
    if (selectedFlourId.startsWith("M")) return `reservationsMM/${masa}`;
    if (selectedFlourId.startsWith("EU")) {
        return `reservationsEurop/${masa}${selectedFlourId}`;
    }
    return null;
};

// Baku is UTC+4 — match the same formula Map.js uses for "today".
const getCurrentDateInBaku = () => {
    const now = new Date();
    const baku = new Date(now.getTime() + 4 * 60 * 60 * 1000);
    return baku.toISOString().split("T")[0];
};

const BRON_AUTH_KEY =
    process.env.REACT_APP_BRON_AUTH_KEY || "784f462b4ee4e847ccfe44db65f51a9c";

// Section -> table layout. Empty string = gap.
const sectionLayouts = {
    L1: {
        rows: [
            ["", "", 16],
            [15, 14, 13, ""],
            [12, 11, 10, ""],
            [9, 8, 7, ""],
            [6, 5, 4, ""],
            ["", 3, 2, 1],
        ],
    },
    L2: {
        rows: [
            ["", 31, 30],
            [29, 28, 27],
            [26, 25, 24],
            [23, 22, 21],
            [20, 19, 18],
        ],
    },
    R1: {
        rows: [
            [34, 35, ""],
            [36, 37, 38],
            [39, 40, 41],
            [42, 43, 44],
            [45, 46, 47],
        ],
    },
    R2: {
        rows: [
            [48, ""],
            ["", 50, 51, 52],
            ["", 53, 54, 55],
            ["", 56, 57, 58],
            ["", 59, 60, 61],
            [62, 63, 64, ""],
        ],
    },
    B1: { rows: [[65]] },
    B2: {
        rows: [
            [71, 68],
            [70, 67],
            [69, 66],
        ],
    },
};

const SECTION_OPTIONS = Object.keys(sectionLayouts).map((k) => ({
    value: k,
    label: k,
}));

const { Title, Text } = Typography;

const CELL_SIZE = 56;
const CELL_GAP = 8;

const TableGridPicker = ({ section, value, onChange }) => {
    if (!section) {
        return (
            <Text type="secondary">Əvvəlcə ərazi (section) seçin.</Text>
        );
    }
    const layout = sectionLayouts[section];
    if (!layout) return null;

    const maxCols = Math.max(...layout.rows.map((r) => r.length));

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: CELL_GAP,
                padding: 8,
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                background: "#fafafa",
                width: "fit-content",
            }}
        >
            {layout.rows.map((row, ri) => (
                <div
                    key={ri}
                    style={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${maxCols}, ${CELL_SIZE}px)`,
                        gap: CELL_GAP,
                    }}
                >
                    {Array.from({ length: maxCols }).map((_, ci) => {
                        const cell = row[ci];
                        if (cell === undefined || cell === "") {
                            return (
                                <div
                                    key={ci}
                                    style={{
                                        width: CELL_SIZE,
                                        height: CELL_SIZE,
                                    }}
                                />
                            );
                        }
                        const selected = String(value) === String(cell);
                        return (
                            <button
                                type="button"
                                key={ci}
                                onClick={() => onChange?.(cell)}
                                style={{
                                    width: CELL_SIZE,
                                    height: CELL_SIZE,
                                    borderRadius: 8,
                                    border: selected
                                        ? "2px solid #1677ff"
                                        : "1px solid #d9d9d9",
                                    background: selected ? "#1677ff" : "#fff",
                                    color: selected ? "#fff" : "#000",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    transition: "all 0.15s",
                                }}
                            >
                                {cell}
                            </button>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

const BronAboneForm = ({ open, onClose, onSuccess }) => {
    const [form] = Form.useForm();
    const [abones, setAbones] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [loadingAbones, setLoadingAbones] = useState(false);
    const [loadingMenu, setLoadingMenu] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const section = Form.useWatch("selectedFlourId", form);
    const masa = Form.useWatch("Masa", form);

    useEffect(() => {
        if (!open) return;

        setLoadingAbones(true);
        axios
            .get(`${CONN_KEY_R}getusers.php`)
            .then((res) => setAbones(Array.isArray(res.data) ? res.data : []))
            .catch((err) => {
                console.error("getusers.php error:", err);
                message.error("Abonent siyahısı alınmadı.");
            })
            .finally(() => setLoadingAbones(false));

        setLoadingMenu(true);
        axios
            .get(`${CONN_KEY}menu.php`)
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                setMenuItems(list);
            })
            .catch((err) => {
                console.error("menu.php error:", err);
                message.error("Menu alınmadı.");
            })
            .finally(() => setLoadingMenu(false));
    }, [open]);

    const menuById = useMemo(() => {
        const m = new Map();
        menuItems.forEach((mi) => {
            const id = mi.menu_id ?? mi.id ?? `${mi.product}-${mi.price}`;
            m.set(String(id), mi);
        });
        return m;
    }, [menuItems]);

    const menuOptions = useMemo(() => {
        const groups = {};
        menuItems.forEach((mi) => {
            const cat = mi.Catagory || mi.category || "Digər";
            if (!groups[cat]) groups[cat] = [];
            const id = String(mi.menu_id ?? mi.id ?? `${mi.product}-${mi.price}`);
            groups[cat].push({
                value: id,
                label: `${mi.product} — ₼${mi.price}`,
            });
        });
        return Object.entries(groups).map(([cat, options]) => ({
            label: cat,
            options,
        }));
    }, [menuItems]);

    const handleMenuItemChange = (rowName, menuId) => {
        const item = menuById.get(String(menuId));
        if (!item) return;
        const current = form.getFieldValue("orders") || [];
        const next = current.map((o, idx) =>
            idx === rowName
                ? {
                      ...o,
                      menuId,
                      product: item.product,
                      price: Number(item.price) || 0,
                      quantity: o?.quantity ?? 1,
                  }
                : o
        );
        form.setFieldValue("orders", next);
    };

    const handleSubmit = async (values) => {
        const managerName = localStorage.getItem("username");
        if (!managerName) {
            message.error("Giriş etmiş admin tapılmadı.");
            return;
        }

        const orders = (values.orders || []).map((o) => ({
            product: o.product,
            quantity: Number(o.quantity),
            price: Number(o.price),
        }));

        const payload = {
            Masa: String(values.Masa),
            aboneId: Number(values.aboneId),
            maneger: managerName,
            workerId: Number(values.workerId ?? 0) || 0,
            selectedFlourId: values.selectedFlourId,
            selectedDay: values.selectedDay.format("YYYY-MM-DD"),
            selectedTime: values.selectedTime
                ? values.selectedTime.format("HH:mm:ss")
                : "23:30:00",
            note: values.note || null,
            orders,
        };

        try {
            setSubmitting(true);
            const res = await axios.post(
                `${CONN_KEY}app123/WebReserv/bronAbone.php`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${BRON_AUTH_KEY}`,
                    },
                }
            );

            // Mirror to Firebase Realtime DB only if the reservation is for today
            // (matches Map.js#fetchDatafirabase behavior — future-date rows are picked
            // up by the periodic sync when the day arrives).
            if (payload.selectedDay === getCurrentDateInBaku()) {
                const path = resolveFirebasePath(payload.Masa, payload.selectedFlourId);
                if (path) {
                    const fbPayload = {
                        Masa: payload.selectedFlourId.startsWith("EU")
                            ? `${payload.Masa}${payload.selectedFlourId}`
                            : payload.Masa,
                        note: payload.note || "",
                        selectedDay: payload.selectedDay,
                        selectedFlourId: payload.selectedFlourId,
                        selectedTime: payload.selectedTime,
                        status: "complete",
                        userId: String(payload.aboneId),
                    };
                    try {
                        await set(ref(database, path), fbPayload);
                    } catch (fbErr) {
                        console.error("Firebase mirror failed:", fbErr);
                    }
                } else {
                    console.warn(
                        `Unrecognized selectedFlourId "${payload.selectedFlourId}" — Firebase mirror skipped.`
                    );
                }
            }

            message.success(
                res.data?.message || "Abonent adına rezerv yaradıldı."
            );
            form.resetFields();
            onSuccess?.(res.data);
            onClose?.();
        } catch (err) {
            const apiMsg =
                err.response?.data?.message ||
                err.message ||
                "Rezerv yaradılmadı.";
            message.error(apiMsg);
            console.error("bronAbone.php error:", err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title="Abonent adına Bron"
            open={open}
            onCancel={() => {
                form.resetFields();
                onClose?.();
            }}
            footer={null}
            width={820}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    selectedDay: dayjs(),
                    selectedTime: dayjs("23:30", "HH:mm"),
                    orders: [],
                }}
            >
                <Form.Item
                    name="aboneId"
                    label="Abonent"
                    rules={[{ required: true, message: "Abonent seçin" }]}
                >
                    <Select
                        showSearch
                        loading={loadingAbones}
                        placeholder="Abonent axtarın..."
                        filterOption={(input, option) =>
                            (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        options={abones.map((a) => ({
                            value: a.id ?? a.userid,
                            label: `${a.username || ""} ${a.surename || ""} (#${ a.userid})`.trim(),
                        }))}
                    />
                </Form.Item>

                <Space size="middle" style={{ display: "flex", flexWrap: "wrap" }}>
                    <Form.Item
                        name="selectedFlourId"
                        label="Ərazi"
                        rules={[{ required: true, message: "Ərazi seçin" }]}
                        style={{ minWidth: 160 }}
                    >
                        <Select
                            options={SECTION_OPTIONS}
                            placeholder="Ərazi"
                            onChange={() => form.setFieldValue("Masa", undefined)}
                        />
                    </Form.Item>

                    <Form.Item
                        name="selectedDay"
                        label="Rezerv tarixi"
                        rules={[{ required: true, message: "Tarix seçin" }]}
                    >
                        <DatePicker
                            format="YYYY-MM-DD"
                            disabledDate={(d) =>
                                d && d.isBefore(dayjs().startOf("day"))
                            }
                        />
                    </Form.Item>

                    <Form.Item name="selectedTime" label="Saat">
                        <TimePicker format="HH:mm" />
                    </Form.Item>

                    <Form.Item
                        name="workerId"
                        label="Resption (workerId)"
                        tooltip="Boş buraxsanız sizin ID istifadə olunur"
                    >
                        <InputNumber min={1} style={{ width: 140 }} />
                    </Form.Item>
                </Space>

                <Form.Item
                    name="Masa"
                    label={`Masa ${masa ? `(seçildi: ${masa})` : ""}`}
                    rules={[{ required: true, message: "Masa seçin" }]}
                >
                    <TableGridPicker section={section} />
                </Form.Item>

                <Form.Item name="note" label="Qeyd">
                    <Input.TextArea rows={2} placeholder="Əlavə qeyd (opsional)" />
                </Form.Item>

                <Title level={5} style={{ marginTop: 8 }}>Sifarişlər</Title>
                <Form.List
                    name="orders"
                    rules={[
                        {
                            validator: async (_, items) => {
                                if (!items || items.length < 1) {
                                    return Promise.reject(
                                        new Error("Ən azı 1 sifariş əlavə edin")
                                    );
                                }
                            },
                        },
                    ]}
                >
                    {(fields, { add, remove }, { errors }) => (
                        <>
                            {fields.map(({ key, name, ...rest }) => (
                                <Space
                                    key={key}
                                    align="baseline"
                                    style={{ display: "flex", marginBottom: 8 }}
                                >
                                    <Form.Item
                                        {...rest}
                                        name={[name, "menuId"]}
                                        rules={[
                                            { required: true, message: "Menu" },
                                        ]}
                                        style={{ marginBottom: 0, minWidth: 320 }}
                                    >
                                        <Select
                                            showSearch
                                            loading={loadingMenu}
                                            placeholder="Menudan seç"
                                            options={menuOptions}
                                            filterOption={(input, option) =>
                                                (option?.label ?? "")
                                                    .toLowerCase()
                                                    .includes(input.toLowerCase())
                                            }
                                            onChange={(v) => handleMenuItemChange(name, v)}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        {...rest}
                                        name={[name, "quantity"]}
                                        rules={[
                                            { required: true, message: "Say" },
                                        ]}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <InputNumber
                                            min={1}
                                            placeholder="Say"
                                            style={{ width: 80 }}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        {...rest}
                                        name={[name, "price"]}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <InputNumber
                                            min={0}
                                            step={0.01}
                                            placeholder="Qiymət (₼)"
                                            style={{ width: 110 }}
                                            disabled
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        {...rest}
                                        name={[name, "product"]}
                                        hidden
                                    >
                                        <Input />
                                    </Form.Item>
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => remove(name)}
                                    />
                                </Space>
                            ))}
                            <Form.Item>
                                <Button
                                    type="dashed"
                                    onClick={() => add({ quantity: 1 })}
                                    block
                                    icon={<PlusOutlined />}
                                >
                                    Sifariş əlavə et
                                </Button>
                                <Form.ErrorList errors={errors} />
                            </Form.Item>
                        </>
                    )}
                </Form.List>

                <Space style={{ marginTop: 12, justifyContent: "flex-end", width: "100%" }}>
                    <Button onClick={() => { form.resetFields(); onClose?.(); }}>
                        Ləğv et
                    </Button>
                    <Button type="primary" htmlType="submit" loading={submitting}>
                        Bron Et
                    </Button>
                </Space>
            </Form>
        </Modal>
    );
};

export default BronAboneForm;
